import { useState } from "react";
import gql from "graphql-tag";
import { useApolloClient, useQuery, QueryHookOptions, QueryOptions } from "@apollo/client";
import { replacePlusFromURLPath } from "@ugg/shared/utils/url-query-params";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/constants/seasons";
import { OVERVIEW_DEFAULT } from "@ugg/shared/query-params/filter-options-profile/overview";
import { RoleN } from "@ugg/shared/utils/role-helpers";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RankScore } from "./profile-ranks";
import { MatchSummary, MatchSummaryInfo } from "./match-summaries";

type SummonerProfileInitParams = Partial<{
  queueType: string;
  season: string;
}>;

export function getDefaultValsForSummonerProfile(region: string, summonerName: string, options: SummonerProfileInitParams = {}) {
  const params = { ...OVERVIEW_DEFAULT, ...(options || {}) };

  const apiParams = {
    regionId: region || "",
    summonerName: summonerName ? decodeURIComponent(replacePlusFromURLPath(summonerName.toLowerCase())) : "",
    queueType: "-1",
    role: String(RoleN.ALL),
    seasonId: String(params.season || INTERNAL_CURRENT_SEASON),
    seasonIdInt: parseInt(params.season || String(INTERNAL_CURRENT_SEASON)),
    championId: "-1",
  };

  if (params.queueType === QueueTypeS.ARAM || params.queueType === QueueTypeS.NEXUS_BLITZ) {
    apiParams.role = String(RoleN.NONE);
  }

  return { apiParams, params };
}

export function useSummonerProfileInit(
  region: string,
  summonerName: string,
  options?: SummonerProfileInitParams,
  queryParams?: QueryHookOptions,
) {
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options);

  return useQuery<SummonerProfileData>(GET_SUMMONER_PROFILE, {
    ...(queryParams || {}),
    variables: apiParams,
  });
}

export function useSummonerProfileInitSimple(
  region: string,
  summonerName: string,
  options?: SummonerProfileInitParams,
  queryParams?: QueryHookOptions,
) {
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options);

  return useQuery<SummonerProfileInitSimpleData>(GET_SUMMONER_PROFILE_SIMPLE, {
    ...(queryParams || {}),
    variables: {
      regionId: apiParams.regionId,
      summonerName: apiParams.summonerName,
      seasonIdInt: apiParams.seasonIdInt,
    },
  });
}

type ManualFetchState<T> = {
  loading: boolean;
  data: T | null;
  error: Error | null;
};

// Did not use useLazyQuery because it has a strange behavior when component rerenders
// Will function similiar to useLazyQuery while ignoring weird rerender behavior
export function useManualSummonerProfileInit(
  region: string,
  summonerName: string,
  options?: SummonerProfileInitParams,
  queryParams?: QueryOptions,
) {
  const client = useApolloClient();
  const [fetchState, setFetchState] = useState<ManualFetchState<SummonerProfileData>>({
    loading: false,
    data: null,
    error: null,
  });
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options);

  const getSummoner = () => {
    setFetchState({ loading: true, data: null, error: null });
    client
      .query<SummonerProfileData>({
        ...(queryParams || {}),
        query: GET_SUMMONER_PROFILE,
        variables: apiParams,
      })
      .then((response) => {
        setFetchState({ loading: false, data: response.data, error: null });
      })
      .catch((error) => {
        setFetchState({ loading: false, data: null, error });
      });
  };

  return [getSummoner, fetchState];
}

export function useManualSummonerProfileInitSimple(
  region: string,
  summonerName: string,
  options?: Record<string, any>,
  queryParams?: QueryOptions,
) {
  const client = useApolloClient();
  const [fetchState, setFetchState] = useState<ManualFetchState<SummonerProfileInitSimpleData>>({
    loading: false,
    data: null,
    error: null,
  });
  const { apiParams } = getDefaultValsForSummonerProfile(region, summonerName, options);

  const getSummoner = () => {
    setFetchState({ loading: true, data: null, error: null });
    client
      .query<SummonerProfileInitSimpleData>({
        query: GET_SUMMONER_PROFILE_SIMPLE,
        variables: apiParams,
        ...(queryParams || {}),
      })
      .then((response) => {
        setFetchState({ loading: false, data: response.data, error: null });
      })
      .catch((error) => {
        setFetchState({ loading: false, data: null, error });
      });
  };

  return [getSummoner, fetchState] as const;
}

export const PlayerInfo = `
  accountIdV3
  accountIdV4
  exodiaUuid
  iconId
  puuidV4
  regionId
  summonerIdV3
  summonerIdV4
  summonerLevel
  summonerName
`;

interface CustomizationData {
  headerBg: string;
  pinnedMatchId: string;
  twitchLink: string;
  twitchName: string;
  twitterLink: string;
  twitterName: string;
  youtubeLink: string;
  youtubeName: string;
  pinnedMatchData: MatchSummary;
}

interface SummonerProfileData {
  fetchProfileRanks: {
    rankScores: RankScore[];
  };
  profileInit: SummonerProfileInitSimple & {
    customizationData: CustomizationData;
  };
}

export const GET_SUMMONER_PROFILE = gql`
  query getSummonerProfile(
    $championId: String!
    $queueType: String!
    $regionId: String!
    $role: String!
    $seasonId: String!
    $seasonIdInt: Int!
    $summonerName: String!
  ) {
    fetchProfileRanks(
      summonerName: $summonerName,
      regionId: $regionId,
      seasonId: $seasonIdInt
    ) {
      rankScores {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
    }
    profileInit(
      championId: $championId,
      queueType: $queueType,
      regionId: $regionId,
      role: $role,
      seasonId: $seasonId,
      summonerName: $summonerName
    ) {
      lastModified
      playerInfo {
        ${PlayerInfo}
      }
      customizationData {
        headerBg
        pinnedMatchId
        twitchLink
        twitchName
        twitterLink
        twitterName
        youtubeLink
        youtubeName
        pinnedMatchData {
          ${MatchSummaryInfo}
        }
      }
    }
  }
`;

export interface SummonerProfileInitSimple {
  lastModified: number;
  playerInfo: {
    accountIdV3: number;
    accountIdV4: string;
    exodiaUuid: string;
    iconId: number;
    puuidV4: string;
    regionId: string;
    summonerIdV3: number;
    summonerIdV4: string;
    summonerLevel: number;
    summonerName: string;
  };
}
interface SummonerProfileInitSimpleData {
  fetchProfileRanks: {
    rankScores: RankScore[];
  };
  profileInitSimple: SummonerProfileInitSimple;
}

export const GET_SUMMONER_PROFILE_SIMPLE = gql`
  query getSummonerProfile(
    $regionId: String!
    $seasonIdInt: Int!
    $summonerName: String!
  ) {
    fetchProfileRanks(
      summonerName: $summonerName,
      regionId: $regionId,
      seasonId: $seasonIdInt
    ) {
      rankScores {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
    }
    profileInitSimple(
      regionId: $regionId,
      summonerName: $summonerName
    ) {
      lastModified
      playerInfo {
        ${PlayerInfo}
      }
    }
  }
`;
